import type { useGT } from 'gt-react';
import type { InventoryPolicyOverrideRow } from 'src/api/rpc/hooks/procurement/useInventoryPolicyOverrides';

// Everything the inventory-policy override list and form decide about a row
// before it renders: which fields it sets, which status that puts it in, and
// how a value reads. Kept out of the components so the rules are unit-testable
// without a DOM. See docs/procurement/lead-time-overrides.md.

type Translate = ReturnType<typeof useGT>;

/** A field is left alone ('keep'), or forced on or off. */
export type TriState = 'keep' | 'on' | 'off';

/** The overridable fields, with how to render and where the base value lives. */
export const OVERRIDE_FIELDS = [
  { key: 'minQuantity', kind: 'num', baseKey: 'baseMinQuantity' },
  { key: 'maxQuantity', kind: 'num', baseKey: 'baseMaxQuantity' },
  { key: 'safetyStock', kind: 'num', baseKey: 'baseSafetyStock' },
  { key: 'minFloor', kind: 'num' },
  { key: 'hardMin', kind: 'num' },
  { key: 'hardMax', kind: 'num' },
  // No base column: the master is the vendor MOQ/increment it replaces.
  { key: 'orderMultiple', kind: 'num' },
  {
    key: 'autoTransferEligible',
    kind: 'bool',
    baseKey: 'baseAutoTransferEligible',
  },
  { key: 'autoBuyEligible', kind: 'bool', baseKey: 'baseAutoBuyEligible' },
] as const;

export type OverrideFieldKey = (typeof OVERRIDE_FIELDS)[number]['key'];

// The field labels have to be string literals for the i18n extractor to see
// them, so they are a switch rather than a `label` on OVERRIDE_FIELDS.
export function overrideFieldLabel(
  key: OverrideFieldKey,
  gt: Translate,
): string {
  switch (key) {
    case 'minQuantity':
      return gt('Min', { context: 'policy override field' });
    case 'maxQuantity':
      return gt('Max', { context: 'policy override field' });
    case 'safetyStock':
      return gt('Safety Stock', { context: 'policy override field' });
    case 'hardMin':
      return gt('Hard min', { context: 'policy override field' });
    case 'hardMax':
      return gt('Hard max', { context: 'policy override field' });
    case 'minFloor':
      return gt('Min Floor', { context: 'policy override field' });
    case 'orderMultiple':
      return gt('Pack Qty', {
        context:
          'policy override field: pack/carton rounding qty that replaces the vendor master',
      });
    case 'autoTransferEligible':
      return gt('Auto-transfer', { context: 'policy override field' });
    case 'autoBuyEligible':
      return gt('Auto-buy', { context: 'policy override field' });
  }
}

const OVERRIDE_FIELD_KEYS: readonly OverrideFieldKey[] = OVERRIDE_FIELDS.map(
  (m) => m.key,
);

/** The fields a not-yet-created override would set. */
export interface PendingOverrideValues {
  minQuantity?: number;
  maxQuantity?: number;
  safetyStock?: number;
  minFloor?: number;
  hardMin?: number;
  hardMax?: number;
  orderMultiple?: number;
  autoTransferEligible?: boolean;
  autoBuyEligible?: boolean;
}

// A scheduled row is unrevoked but its window has not started, so nothing it
// sets applies yet; it is still the buyer's to edit or revoke. 'inactive'
// covers the rest: revoked, replaced, or lapsed.
export type PolicyOverrideStatus =
  'in_effect' | 'shadowed' | 'scheduled' | 'inactive';

/** Whether a row still has an edit / revoke: live now, or waiting to start. */
export function isPolicyOverrideActionable(
  status: PolicyOverrideStatus,
): boolean {
  return status !== 'inactive';
}

export function fmtFieldValue(
  kind: 'num' | 'bool',
  v: number | boolean | null,
  gt: Translate,
): string {
  if (v == null) return '—';
  if (kind === 'bool') {
    return v
      ? gt('Eligible', { context: 'auto eligibility value' })
      : gt('Not eligible', { context: 'auto eligibility value' });
  }
  return Number(v).toLocaleString();
}

export function triToBool(v: TriState): boolean | undefined {
  if (v === 'keep') return undefined;
  return v === 'on';
}

/** The inverse of {@link triToBool}: an override's stored value as a control state. */
export function boolToTri(v: boolean | null): TriState {
  if (v == null) return 'keep';
  return v ? 'on' : 'off';
}

export function numOrUndefined(v: string): number | undefined {
  const t = v.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** The inverse of {@link numOrUndefined}: a stored value as a text-input value. */
export function numText(v: number | null): string {
  return v == null ? '' : String(v);
}

export function policyStatusLabel(
  status: PolicyOverrideStatus,
  gt: Translate,
): string {
  switch (status) {
    case 'in_effect':
      return gt('in effect', { context: 'policy override status badge' });
    case 'shadowed':
      return gt('shadowed', {
        context:
          'policy override status badge for an active-but-superseded override',
      });
    case 'scheduled':
      return gt('scheduled', { context: 'policy override status badge' });
    case 'inactive':
      return gt('inactive', { context: 'policy override status badge' });
  }
}

export function policyStatusBadgeClasses(status: PolicyOverrideStatus): string {
  if (status === 'in_effect') return 'bg-green-100 text-green-700';
  if (status === 'shadowed' || status === 'scheduled') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'text-gray-400';
}

/** A shadowed row is struck through: it is live, but nothing it sets applies. */
export function policyHeadlineClasses(status: PolicyOverrideStatus): string {
  return status === 'shadowed'
    ? 'font-medium text-gray-400 line-through'
    : 'font-medium';
}

// What a field falls back to once the override that sets it is revoked: the
// next active override that sets it, else the base value. `overrides` arrives
// created_at DESC, so the first active match is the newest one.
export function nextActiveSetter(
  overrides: readonly InventoryPolicyOverrideRow[],
  excludeId: string,
  key: OverrideFieldKey,
): InventoryPolicyOverrideRow | undefined {
  return overrides.find(
    (o) => o.isActive && o.id !== excludeId && o[key] != null,
  );
}

/** Where a revoked field's value will come from instead. */
export function revokeFallbackSource(
  fallback: InventoryPolicyOverrideRow | undefined,
  gt: Translate,
  hasBaseValue = true,
  key?: OverrideFieldKey,
): string {
  if (!fallback) {
    if (hasBaseValue) {
      return gt('forecast / base', { context: 'revoke fallback source' });
    }
    if (key === 'hardMin' || key === 'hardMax')
      return gt('no limit', { context: 'revoke fallback source' });
    return key === 'orderMultiple'
      ? gt('vendor master', { context: 'revoke fallback source' })
      : gt('no floor', { context: 'revoke fallback source' });
  }
  if (!fallback.createdByUserName) {
    return gt('another override', { context: 'revoke fallback source' });
  }
  return gt('override · {who}', {
    context: 'revoke fallback source',
    who: fallback.createdByUserName,
  });
}

/** The fields this override sets, as the row's headline. */
export function describeOverride(
  o: InventoryPolicyOverrideRow,
  gt: Translate,
): string {
  const parts: string[] = [];
  if (o.minQuantity != null) parts.push(`min=${o.minQuantity}`);
  if (o.maxQuantity != null) parts.push(`max=${o.maxQuantity}`);
  if (o.safetyStock != null) {
    parts.push(
      gt('safety stock={value}', {
        context: 'policy override summary field',
        value: o.safetyStock,
      }),
    );
  }
  if (o.minFloor != null) {
    parts.push(
      gt('min floor={value}', {
        context: 'policy override summary field',
        value: o.minFloor,
      }),
    );
  }
  for (const key of ['hardMin', 'hardMax'] as const) {
    if (o[key] != null) parts.push(`${overrideFieldLabel(key, gt)}=${o[key]}`);
  }
  if (o.orderMultiple != null) {
    parts.push(
      gt('pack qty={value}', {
        context: 'policy override summary field',
        value: o.orderMultiple,
      }),
    );
  }
  if (o.autoTransferEligible != null) {
    parts.push(`auto-transfer=${o.autoTransferEligible ? 'on' : 'off'}`);
  }
  if (o.autoBuyEligible != null) {
    parts.push(`auto-buy=${o.autoBuyEligible ? 'on' : 'off'}`);
  }
  return parts.join(', ') || gt('(no fields)', { context: 'policy override' });
}

// Per-field, last-writer-wins-among-active (mirrors the backend resolver):
//   - scheduled → unrevoked, window not started yet
//   - inactive  → revoked, or lapsed
//   - in-effect → active AND the most-recent active setter of ≥1 of its fields
//   - shadowed  → active but every field it sets is overridden by a newer
//                 active override (it resumes if that newer one expires)
// `overrides` arrives ordered created_at DESC, so the first active row that
// sets a field is that field's current winner.
export function overrideStatuses(
  overrides: readonly InventoryPolicyOverrideRow[],
  now: Date = new Date(),
): Map<string, PolicyOverrideStatus> {
  const active = overrides.filter((o) => o.isActive);
  const winnerId = new Map<OverrideFieldKey, string>();
  for (const f of OVERRIDE_FIELD_KEYS) {
    const winner = active.find((o) => o[f] != null);
    if (winner) winnerId.set(f, winner.id);
  }
  const statuses = new Map<string, PolicyOverrideStatus>();
  for (const o of overrides) {
    if (!o.isActive) {
      const scheduled =
        o.deactivatedAt === null &&
        new Date(o.startsAt).getTime() > now.getTime();
      statuses.set(o.id, scheduled ? 'scheduled' : 'inactive');
      continue;
    }
    const wins = OVERRIDE_FIELD_KEYS.some(
      (f) => o[f] != null && winnerId.get(f) === o.id,
    );
    statuses.set(o.id, wins ? 'in_effect' : 'shadowed');
  }
  return statuses;
}

export function invalidHardStops(values: PendingOverrideValues): boolean {
  const { hardMin, hardMax } = values;
  if (hardMin !== undefined && hardMin < 0) return true;
  if (hardMax !== undefined && hardMax < 0) return true;
  return hardMin !== undefined && hardMax !== undefined && hardMin > hardMax;
}
